/* RESEARCH_STABLE_API_V1 */
const API_ROOT = (
  process.env.REACT_APP_NEEDS_API_URL || "http://127.0.0.1:8010/api"
).replace(/\/$/, "");

async function get(path) {
  const response = await fetch(`${API_ROOT}${path}`);
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch (_) {}
    throw new Error(`Research API: ${detail}`);
  }
  return response.json();
}

function qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

// V25_10_7_SAME_CAPABILITY_DUAL_ORIENTATION_DATA_ADAPTER_V1
export function normalizeSameCapabilityDualOrientationResponse(payload = {}) {
  const formations = Array.isArray(payload?.formations)
    ? payload.formations.map((formation) => ({
        ...formation,
        affords: {
          ...(formation?.affords || {}),
          projectionIds: Array.isArray(formation?.affords?.projectionIds)
            ? [...formation.affords.projectionIds]
            : [],
        },
        constrains: {
          ...(formation?.constrains || {}),
          projectionIds: Array.isArray(formation?.constrains?.projectionIds)
            ? [...formation.constrains.projectionIds]
            : [],
        },
      }))
    : [];

  return {
    semanticVersion: payload?.semanticVersion || "V25.10",
    count: formations.length,
    formations,
  };
}

export const sameCapabilityDualOrientationLoadState = Object.freeze({
  loading: false,
  error: null,
  data: null,
});

export const sameCapabilityDualOrientationLoading = () => ({
  loading: true,
  error: null,
  data: null,
});

export const sameCapabilityDualOrientationLoaded = (data) => ({
  loading: false,
  error: null,
  data,
});

export const sameCapabilityDualOrientationFailed = (error) => ({
  loading: false,
  error: error?.message || String(error),
  data: null,
});

// SYSTEMIC_TENSION_REACT_READ_ADAPTER_V25_176
export function buildSystemicTensionQuery({
  configurationId = null,
  capabilityId = null,
  limit = 100,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (configurationId) params.set("configuration_id", configurationId);
  if (capabilityId) params.set("capability_id", capabilityId);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const query = params.toString();
  return `/research/systemic-tensions${query ? `?${query}` : ""}`;
}

export const researchApi = {
  getSystemicTensions: (options = {}) =>
    get(buildSystemicTensionQuery(options)),

  getSameCapabilityDualOrientation: async () =>
    normalizeSameCapabilityDualOrientationResponse(
      await get("/research/same-capability-dual-orientation")
    ),

  overview: () => get("/research/overview"),
  coverage: () => get("/research/coverage"),

  listStates: ({
    capabilityId,
    multiRootOnly = false,
    limit = 100,
    offset = 0,
  } = {}) =>
    get(
      `/research/states${qs({
        capability_id: capabilityId,
        multi_root_only: multiRootOnly,
        limit,
        offset,
      })}`
    ),

  getState: (id) => get(`/research/states/${encodeURIComponent(id)}`),


  getStateExplanation: (id) =>
    get(`/research/states/${encodeURIComponent(id)}/explanation`),

  getCapabilityExplanation: (id, { configurationId } = {}) =>
    get(
      `/research/capabilities/${encodeURIComponent(id)}/explanation${qs({
        configuration_id: configurationId,
      })}`
    ),


  listDeltas: ({ capabilityId, limit = 100, offset = 0 } = {}) =>
    get(
      `/research/deltas${qs({
        capability_id: capabilityId,
        limit,
        offset,
      })}`
    ),

  getDelta: (id) => get(`/research/deltas/${encodeURIComponent(id)}`),

  getPathway: (id) => get(`/research/pathways/${encodeURIComponent(id)}`),

  getRoot: (id, { sourceLimit = 50, atomLimit = 50 } = {}) =>
    get(
      `/research/roots/${encodeURIComponent(id)}${qs({
        source_limit: sourceLimit,
        atom_limit: atomLimit,
      })}`
    ),
};

export default researchApi;
